"use client";

import { NetworkEndpoints } from "@/configs/NetworkEndpoints";
import { useEffect, useState } from "react";
import { OfficePriceBenchmarks } from "./officePriceBenchmark";

/**
 * Typical private-office prices around `locationId`, or null while loading,
 * when there is no location, for conventional offices (no ranges are built for
 * them yet) and when the lookup fails. Every null means the same thing to the
 * form: show the plain slider without advice.
 */
export const useOfficePriceBenchmarks = (
    locationId: string | undefined,
    officeType: "SERVICED" | "CONVENTIONAL"
): OfficePriceBenchmarks | null => {
    const [benchmarks, setBenchmarks] = useState<{
        key: string;
        value: OfficePriceBenchmarks;
    } | null>(null);
    const key = `${locationId}|${officeType}`;

    useEffect(() => {
        if (!locationId || officeType !== "SERVICED") return;
        let active = true;
        fetch(NetworkEndpoints.officePriceBenchmark(locationId, officeType))
            .then((res) => (res.ok ? res.json() : null))
            .then((json: OfficePriceBenchmarks | null) => {
                if (active && json) setBenchmarks({ key, value: json });
            })
            .catch(() => {
                // Advice is a nicety; without it the slider works as before.
            });
        return () => {
            active = false;
        };
    }, [locationId, officeType, key]);

    // Only for the location and type it was fetched for, so a stale answer
    // never advises on the wrong office.
    return benchmarks?.key === key ? benchmarks.value : null;
};
